/**
 * TOTP (Time-based One-Time Password) Implementation
 * Replaces Speakeasy with pure JavaScript implementation
 * Compatible with Google Authenticator, Authy, Microsoft Authenticator, etc.
 */

import crypto from "crypto-js";

/**
 * Generate a random base32 secret for TOTP
 * @returns {string} Base32 encoded secret
 */
export function generateSecret() {
  const buffer = new Uint8Array(20);
  window.crypto.getRandomValues(buffer);
  return base32Encode(buffer);
}

/**
 * Generate TOTP token for a given secret
 * @param {string} secret - Base32 encoded secret
 * @param {number} window - Time window (default: current time)
 * @returns {string} 6-digit TOTP code
 */
export function generateToken(
  secret,
  window = Math.floor(Date.now() / 1000 / 30)
) {
  const key = base32Decode(secret);
  const time = new Uint8Array(8);

  for (let i = 7; i >= 0; i--) {
    time[i] = window & 0xff;
    window = window >> 8;
  }

  const hmac = crypto.HmacSHA1(
    crypto.lib.WordArray.create(time),
    crypto.lib.WordArray.create(new Uint8Array(key))
  );

  const hmacBytes = hmacToBytes(hmac);
  const offset = hmacBytes[19] & 0xf;

  const code =
    ((hmacBytes[offset] & 0x7f) << 24) |
    ((hmacBytes[offset + 1] & 0xff) << 16) |
    ((hmacBytes[offset + 2] & 0xff) << 8) |
    (hmacBytes[offset + 3] & 0xff);

  return String(code % 1000000).padStart(6, "0");
}

/**
 * Verify a TOTP token
 * @param {string} token - 6-digit code to verify
 * @param {string} secret - Base32 encoded secret
 * @param {number} windowSize - Number of windows to check (default: 1)
 * @returns {boolean} True if token is valid
 */
export function verifyToken(token, secret, windowSize = 1) {
  const currentWindow = Math.floor(Date.now() / 1000 / 30);

  for (let i = -windowSize; i <= windowSize; i++) {
    const generatedToken = generateToken(secret, currentWindow + i);
    if (generatedToken === token) {
      return true;
    }
  }

  return false;
}

/**
 * Generate otpauth:// URI for QR code
 * @param {string} secret - Base32 encoded secret
 * @param {string} accountName - User's email or username
 * @param {string} issuer - App name (default: "Business App")
 * @returns {string} otpauth URI
 */
export function generateOtpAuthUrl(
  secret,
  accountName,
  issuer = "Business App"
) {
  const encodedIssuer = encodeURIComponent(issuer);
  const encodedAccount = encodeURIComponent(accountName);

  return `otpauth://totp/${encodedIssuer}:${encodedAccount}?secret=${secret}&issuer=${encodedIssuer}&algorithm=SHA1&digits=6&period=30`;
}

/**
 * Base32 encoding (RFC 4648)
 */
function base32Encode(buffer) {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
  let bits = 0;
  let value = 0;
  let output = "";

  for (let i = 0; i < buffer.length; i++) {
    value = (value << 8) | buffer[i];
    bits += 8;

    while (bits >= 5) {
      output += alphabet[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }

  if (bits > 0) {
    output += alphabet[(value << (5 - bits)) & 31];
  }

  return output;
}

/**
 * Base32 decoding (RFC 4648)
 */
function base32Decode(base32) {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
  let bits = 0;
  let value = 0;
  let index = 0;
  const output = new Uint8Array(Math.ceil((base32.length * 5) / 8));

  for (let i = 0; i < base32.length; i++) {
    const idx = alphabet.indexOf(base32[i].toUpperCase());
    if (idx === -1) continue;

    value = (value << 5) | idx;
    bits += 5;

    if (bits >= 8) {
      output[index++] = (value >>> (bits - 8)) & 255;
      bits -= 8;
    }
  }

  return Array.from(output.slice(0, index));
}

/**
 * Convert CryptoJS WordArray to byte array
 */
function hmacToBytes(hmac) {
  const words = hmac.words;
  const sigBytes = hmac.sigBytes;
  const bytes = [];

  for (let i = 0; i < sigBytes; i++) {
    const byte = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
    bytes.push(byte);
  }

  return bytes;
}
